import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import CategoryBadge from "../../components/doctors/CategoryBadge";
import FeedbackBadge from "../../components/visits/FeedbackBadge";
import AdHocBadge from "../../components/visits/AdHocBadge";
import OffListBadge from "../../components/visits/OffListBadge";
import {
  ArrowLeft,
  Pencil,
  Clock,
  CalendarDays,
  Pill,
  PackageOpen,
  Stethoscope,
  MapPin,
  CheckCircle2,
  Sparkles,
  Loader2,
  UserCheck,
  TriangleAlert,
  Zap,
} from "lucide-react";
import { todayIST, formatISTDate, formatISTTime } from "../../lib/datetime";
import { isOverdue } from "../../lib/vocabulary";

export default function VisitDetailPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const justLogged = !!location.state?.justLogged;
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/doctor-visits/${id}`)
      .then(({ data }) => !cancelled && setVisit(data))
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-callisto-muted text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading visit…
      </div>
    );
  }

  if (error || !visit) {
    return (
      <div className="bg-white border border-callisto rounded-xl p-8 text-center">
        <h2 className="text-lg font-bold text-callisto-navy">Visit unavailable</h2>
        <p className="mt-2 text-sm text-callisto-muted">{error || "Not found"}</p>
        <Link
          to="/doctor-visits"
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-callisto-gold hover:underline"
        >
          <ArrowLeft size={14} /> Back to visits
        </Link>
      </div>
    );
  }

  // Whoever logged the call owns it — Sales Officer or Sales Manager.
  const isOwner = visit.sales_officer_id === user?.id;
  const isSameDay = visit.visit_date === todayIST();
  const canEdit = isOwner && isSameDay;

  // RC-11: the follow-up date was printed raw, with nothing comparing it to
  // today — an overdue follow-up looked exactly like a future one.
  const followUp = visit.follow_up_required ? visit.follow_up_date : null;
  const followUpOverdue = isOverdue(followUp, todayIST());

  return (
    <div className="space-y-6" data-testid="visit-detail-page">
      {justLogged && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 px-4 py-3 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold text-sm">Visit submitted</p>
            <p className="text-xs">Great work — this report is now part of today's DCR.</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link
          to="/doctor-visits"
          className="inline-flex items-center gap-1 text-sm text-callisto-muted hover:text-callisto-navy"
        >
          <ArrowLeft size={14} />
          Back to visits
        </Link>
        {canEdit && (
          <Button
            variant="outline"
            onClick={() => navigate(`/doctor-visits/${id}/edit`)}
            className="h-10 border-callisto"
            data-testid="visit-edit-button"
          >
            <Pencil className="h-4 w-4 mr-1.5" />
            Edit visit
          </Button>
        )}
      </div>

      {/* Hero */}
      <div className="bg-white border border-callisto rounded-xl p-5 md:p-7">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-xl bg-callisto-navy text-callisto-gold flex items-center justify-center text-base font-bold shrink-0">
            <Stethoscope className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-callisto-navy">
                {visit.doctor_name}
              </h1>
              {visit.doctor_category && <CategoryBadge category={visit.doctor_category} size="lg" />}
              <FeedbackBadge feedback={visit.doctor_feedback} />
              <AdHocBadge visit={visit} size="lg" />
              <OffListBadge visit={visit} size="lg" />
              {visit.is_edited && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-callisto-gold">
                  <Sparkles size={11} /> edited
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-callisto-muted flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <CalendarDays size={14} />
                {formatISTDate(visit.visit_date)}
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock size={14} />
                {formatISTTime(visit.submitted_at)}
              </span>
              <span className="inline-flex items-center gap-1">
                <UserCheck size={14} />
                {visit.sales_officer_name}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-callisto rounded-xl p-5 md:p-7 space-y-6">
          <Section title="Products discussed" Icon={Pill}>
            {visit.products_discussed?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {visit.products_discussed.map((p) => (
                  <span
                    key={p}
                    className="text-xs font-semibold px-2.5 py-1 rounded-full bg-callisto-navy/5 text-callisto-navy border border-callisto"
                  >
                    {p}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-callisto-muted">No products discussed.</p>
            )}
          </Section>

          <Section title="Samples given" Icon={PackageOpen}>
            {visit.samples_given?.length ? (
              <ul className="divide-y divide-callisto">
                {visit.samples_given.map((s, idx) => (
                  <li key={idx} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-callisto-body">{s.product}</span>
                    <span className="font-semibold text-callisto-navy">×{s.quantity}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-callisto-muted">None.</p>
            )}
          </Section>

          {visit.visit_notes && (
            <Section title="Notes">
              <p className="text-sm text-callisto-body whitespace-pre-wrap">
                {visit.visit_notes}
              </p>
            </Section>
          )}
        </div>

        <div className="bg-white border border-callisto rounded-xl p-5 md:p-7 space-y-5">
          {/* PLANNING (§2). Three states, the third said as a gap: a row from
              before the field existed is "Not recorded", never "unplanned". */}
          <Section title="Planning" Icon={Zap}>
            <p className="text-sm font-semibold text-callisto-navy" data-testid="visit-planning">
              {visit.is_planned === true
                ? "In the tour plan"
                : visit.is_planned === false
                  ? visit.deviation_reason
                    ? "Ad hoc call"
                    : "Not in the tour plan"
                  : "Not recorded"}
            </p>
            {visit.is_planned === false && visit.deviation_reason && (
              <p className="text-xs text-callisto-muted" data-testid="visit-planning-reason">
                Reason: <span className="font-semibold text-callisto-body">{visit.deviation_reason}</span>
              </p>
            )}
          </Section>

          <Section title="Follow-up" Icon={CalendarDays}>
            {visit.follow_up_required ? (
              <div data-testid="visit-followup" data-overdue={followUpOverdue ? "true" : "false"}>
                <p className="text-sm font-semibold text-callisto-navy">Yes</p>
                <p className="text-xs text-callisto-muted">
                  Scheduled for{" "}
                  <span className="font-semibold text-callisto-body">
                    {formatISTDate(followUp)}
                  </span>
                </p>
                {followUpOverdue && (
                  <p
                    className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-red-700"
                    data-testid="visit-followup-overdue"
                  >
                    <TriangleAlert size={11} /> Overdue — this follow-up date has passed
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-callisto-muted">Not required.</p>
            )}
          </Section>

          <Section title="Location" Icon={MapPin}>
            {visit.gps_location ? (
              <p className="text-xs font-mono text-callisto-body">
                {visit.gps_location.lat.toFixed(5)}, {visit.gps_location.lng.toFixed(5)}
              </p>
            ) : (
              <p className="text-sm text-callisto-muted">Not captured.</p>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, Icon, children }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3 flex items-center gap-1.5">
        {Icon && <Icon size={12} />}
        {title}
      </p>
      {children}
    </div>
  );
}
